import { prepareRegisterRequestBody } from '../api/assetPayloadNormalize';
import {
  buildAssetOwnerPayoutProfilePatchBody,
  pickAssetOwnerPayoutFields,
  validateAssetOwnerPayoutFields,
} from './assetOwnerPayoutBank';
import { ISRAEL_COUNTRY_ID } from './assetCountryCurrency';
import { resolveProfileRecord } from './profileDisplaySummary';
import type { MessageKey } from '../i18n/messages';

export {
  buildAssetOwnerPayoutPatchBodyFromChanges,
  mergePayoutFormPayload,
} from './assetOwnerPayoutPartialPatch';

export type AssetOwnerProfileEditFields = {
  businessName: string;
  businessId: string;
  email: string;
  contactFirstName: string;
  contactLastName: string;
  phoneNumber: string;
  phoneNumber2: string;
};

export function extractProfileCountryId(data: unknown): number | null {
  const record = resolveProfileRecord(data);
  if (!record) {
    return null;
  }
  const readCountryId = (value: unknown): number | null => {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }
    if (typeof value === 'string' && value.trim()) {
      const parsed = Number(value.trim());
      return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
  };

  const topLevel = readCountryId(record.country_id);
  if (topLevel != null) {
    return topLevel;
  }

  for (const key of ['asset_owner', 'assetOwner', 'business_client', 'businessClient'] as const) {
    const nested = record[key];
    if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
      const nestedCountry = readCountryId((nested as Record<string, unknown>).country_id);
      if (nestedCountry != null) {
        return nestedCountry;
      }
    }
  }

  return null;
}

/**
 * Profile country for locale/payment rules. Existing accounts that never stored
 * `country_id` are treated as Israel.
 */
export function resolveProfileCountryIdOrIsrael(data: unknown): number {
  return extractProfileCountryId(data) ?? ISRAEL_COUNTRY_ID;
}

export function assetOwnerPayoutPayloadFromProfile(data: unknown): Record<string, unknown> {
  const record = resolveProfileRecord(data);
  if (!record) {
    return {};
  }
  const countryId = extractProfileCountryId(data);
  return {
    ...pickAssetOwnerPayoutFields(record),
    ...(countryId != null ? { country_id: countryId } : {}),
  };
}

export function buildAssetOwnerProfileEditPatchBody(
  fields: AssetOwnerProfileEditFields,
  options?: { logo?: string; profileImage?: string },
): Record<string, unknown> {
  const body: Record<string, unknown> = {
    business_name: fields.businessName.trim(),
    business_id: fields.businessId.trim(),
    contact_first_name: fields.contactFirstName.trim(),
    contact_last_name: fields.contactLastName.trim(),
    first_name: fields.contactFirstName.trim(),
    last_name: fields.contactLastName.trim(),
    phone_number: fields.phoneNumber.trim(),
    phone_number_2: fields.phoneNumber2.trim(),
  };
  if (fields.email.trim()) {
    body.email = fields.email.trim();
  }
  if (options?.profileImage?.trim()) {
    body.profile_image = options.profileImage.trim();
  }
  if (options?.logo?.trim()) {
    body.logo = options.logo.trim();
  }
  const prepared = prepareRegisterRequestBody(body);
  return typeof prepared === 'object' && prepared !== null && !Array.isArray(prepared)
    ? (prepared as Record<string, unknown>)
    : body;
}

export function buildAssetOwnerPayoutPatchBody(
  payoutPayload: Record<string, unknown>,
): Record<string, unknown> {
  return buildAssetOwnerPayoutProfilePatchBody(payoutPayload);
}

export function validateAssetOwnerPayoutPatch(
  payoutPayload: Record<string, unknown>,
): MessageKey | null {
  return validateAssetOwnerPayoutFields(payoutPayload);
}

